import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Alert,
  AppLayout,
  Box,
  Button,
  ContentLayout,
  Header,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";

import SideNav from "../../Home/SideNav";
import {
  BYOC_NAV_LABEL,
  BYOC_ROUTE,
  getByocHistoryRoute,
  FAMILY_SUPPORTS_DYNAMIC_DATA,
  PAYLOAD_FAMILY_LABEL,
  SOLUTION_STATUS,
  getStatusDisplay,
  isOpenable,
  isRetired,
} from "./constants";
import { getSubmissionStatus, listSolutions, pollDeploy, submitUpload } from "./api";
import UploadModal from "./UploadModal";
import StatusPanel from "./StatusPanel";
import DynamicDataModal from "./DynamicDataModal";

/** "2026-10-11T09:14:00Z" → "2026-10-11". The time is noise in a list. */
export const formatDay = (iso) => (iso ? String(iso).slice(0, 10) : "—");

export const formatUpdated = (solution, currentUser) => {
  if (!solution || !solution.updatedAt) return "—";
  const who = solution.updatedBy === currentUser ? "you" : solution.updatedBy;
  return who ? `${formatDay(solution.updatedAt)} ${who}` : formatDay(solution.updatedAt);
};

export const matchesFilter = (solution, filterText) => {
  if (!filterText) return true;
  const needle = filterText.toLowerCase();
  return [solution.name, solution.owner, PAYLOAD_FAMILY_LABEL[solution.family]]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(needle));
};

/**
 * BYOC landing page.
 *
 * Empty on a first visit, a list thereafter — the list is the primary surface
 * because it is where updates start, not a secondary view.
 *
 * The timing difference between families is stated on the empty state rather
 * than discovered later: apps are live in minutes, scripts need a code review
 * and take days. It is the most surprising thing about the platform and the
 * cheapest place to say it is before anyone uploads.
 */
const BYOC = () => {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [navOpen, setNavOpen] = useState(true);
  const routerHistory = useHistory();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusSolution, setStatusSolution] = useState(null);
  const [dynamicDataSolution, setDynamicDataSolution] = useState(null);

  const userId = useSelector((state) => (state && state.auth ? state.auth.userId : null));
  const currentUser = userId || "";

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listSolutions();
      setSolutions(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setLoadError(
        error && error.message ? error.message : "Could not load what you've onboarded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Deprecated solutions stay in the registry for the record but are hidden
  // here — retiring something is a soft delete, not a disappearance.
  const visibleSolutions = useMemo(
    () =>
      solutions.filter(
        (solution) =>
          (showRetired || !isRetired(solution)) && matchesFilter(solution, filterText)
      ),
    [solutions, filterText, showRetired]
  );

  const retiredCount = useMemo(() => solutions.filter(isRetired).length, [solutions]);

  const handleSubmit = async (file, config) => {
    const response = await submitUpload(file, config);
    setUploadOpen(false);

    const status = await getSubmissionStatus(response.id, response.transactionId);
    setSubmission({ ...response, ...status, family: config.family, name: config.name });
    setStatusSolution(null);
    setStatusOpen(true);
    load();

    // Publishing runs asynchronously — the mutation above only handed the work
    // off. Follow the transaction item so the panel shows real progress, and
    // reload the list at the end so the row flips to Live.
    if (response.transactionId) {
      pollDeploy(response.id, response.transactionId, {
        onTick: async (item) => {
          const next = await getSubmissionStatus(response.id, response.transactionId);
          setSubmission((previous) =>
            previous && previous.id === response.id
              ? { ...previous, ...next, deployStatus: item.deploy_status }
              : previous
          );
        },
      })
        .then(() => load())
        // A polling failure must not surface as a deploy failure: the publish
        // may well have succeeded, and the email is the source of truth.
        .catch(() => {});
    }
  };

  const openStatus = async (solution) => {
    setStatusSolution(solution);
    if (solution.pipelineState === SOLUTION_STATUS.LIVE) {
      setSubmission(null);
    } else {
      const status = await getSubmissionStatus(solution.id, solution.transactionId);
      setSubmission({ ...status, family: solution.family, name: solution.name });
    }
    setStatusOpen(true);
  };

  const handleDynamicDataChanged = (response) => {
    setSolutions((previous) =>
      previous.map((solution) =>
        solution.id === response.id
          ? { ...solution, dynamicDataEnabled: response.dynamicDataEnabled }
          : solution
      )
    );
    setDynamicDataSolution((previous) =>
      previous && previous.id === response.id
        ? { ...previous, dynamicDataEnabled: response.dynamicDataEnabled }
        : previous
    );
  };

  const columnDefinitions = [
    {
      id: "name",
      header: "Name",
      cell: (solution) => (
        <Link
          href="#"
          onFollow={(event) => {
            event.preventDefault();
            // Its own page rather than a modal, so the URL can be shared —
            // "what happened to this app" is a thing people paste into a
            // ticket, and a modal has no address.
            routerHistory.push(getByocHistoryRoute(solution.id));
          }}
        >
          {solution.name}
        </Link>
      ),
      sortingField: "name",
    },
    {
      id: "type",
      header: "Type",
      cell: (solution) => PAYLOAD_FAMILY_LABEL[solution.family] || "—",
    },
    {
      id: "status",
      header: "Status",
      cell: (solution) => {
        const display = getStatusDisplay(solution);
        return <StatusIndicator type={display.type}>{display.label}</StatusIndicator>;
      },
    },
    {
      id: "updated",
      header: "Last updated",
      cell: (solution) => formatUpdated(solution, currentUser),
    },
    {
      id: "actions",
      header: "",
      cell: (solution) => (
        <SpaceBetween direction="horizontal" size="xs">
          {isOpenable(solution) && (
            <Button variant="inline-link" href={solution.link} target="_blank" iconName="external">
              Open
            </Button>
          )}
          <Button variant="inline-link" onClick={() => openStatus(solution)}>
            Status
          </Button>
          {FAMILY_SUPPORTS_DYNAMIC_DATA[solution.family] && (
            <Button variant="inline-link" onClick={() => setDynamicDataSolution(solution)}>
              Data
            </Button>
          )}
        </SpaceBetween>
      ),
    },
  ];

  const emptyState = (
    <Box textAlign="center" padding={{ vertical: "xxl" }}>
      <SpaceBetween size="m">
        <Box variant="strong">You haven&apos;t onboarded anything yet.</Box>
        <Box variant="p" color="text-body-secondary">
          Upload a web app or a Python script. We work out how to run it, deploy it, and send you the
          link.
        </Box>
        <Box variant="p" color="text-body-secondary">
          Apps go live in minutes. Scripts need a code review first, so they take a few days.
        </Box>
        <Button variant="primary" onClick={() => setUploadOpen(true)}>
          Upload your first one
        </Button>
      </SpaceBetween>
    </Box>
  );

  const content = (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Upload what you built — a web app or a Python script — and we host it behind a Midway link."
          actions={
            <Button variant="primary" onClick={() => setUploadOpen(true)} data-testid="byoc-upload-button">
              Upload
            </Button>
          }
        >
          {BYOC_NAV_LABEL} — Bring Your Own Code
        </Header>
      }
    >
      <SpaceBetween size="l">
        {loadError && (
          <Alert type="error" header="Could not load your solutions" action={<Button onClick={load}>Retry</Button>}>
            {loadError}
          </Alert>
        )}

        <Table
          items={visibleSolutions}
          columnDefinitions={columnDefinitions}
          loading={loading}
          loadingText="Loading what you've onboarded"
          trackBy="id"
          variant="container"
          empty={solutions.length === 0 && !loading ? emptyState : <Box textAlign="center">No matches.</Box>}
          filter={
            solutions.length > 0 ? (
              <SpaceBetween direction="horizontal" size="s">
              <TextFilter
                filteringText={filterText}
                filteringPlaceholder="Find a solution"
                onChange={({ detail }) => setFilterText(detail.filteringText)}
                countText={`${visibleSolutions.length} match${visibleSolutions.length === 1 ? "" : "es"}`}
              />
              {retiredCount > 0 && (
                <Button
                  variant="inline-link"
                  onClick={() => setShowRetired((previous) => !previous)}
                  data-testid="byoc-toggle-retired"
                >
                  {showRetired ? "Hide" : "Show"} {retiredCount} deprecated
                </Button>
              )}
              </SpaceBetween>
            ) : undefined
          }
          header={
            solutions.length > 0 ? (
              <Header counter={`(${solutions.length})`}>Onboarded</Header>
            ) : undefined
          }
          data-testid="byoc-solutions-table"
        />
      </SpaceBetween>

      <UploadModal
        visible={uploadOpen}
        onDismiss={() => setUploadOpen(false)}
        onSubmit={handleSubmit}
        solutions={solutions}
        currentUser={currentUser}
      />

      <StatusPanel
        visible={statusOpen}
        onDismiss={() => setStatusOpen(false)}
        solution={statusSolution}
        submission={submission}
        onUploadFix={() => {
          setStatusOpen(false);
          setUploadOpen(true);
        }}
      />

      <DynamicDataModal
        visible={Boolean(dynamicDataSolution)}
        onDismiss={() => setDynamicDataSolution(null)}
        solution={dynamicDataSolution}
        onChanged={handleDynamicDataChanged}
      />
    </ContentLayout>
  );

  return (
    <AppLayout
      content={content}
      contentType="default"
      toolsHide
      navigationOpen={navOpen}
      onNavigationChange={() => setNavOpen(!navOpen)}
      navigation={<SideNav activeHref={BYOC_ROUTE} />}
    />
  );
};

export default BYOC;
